// Theme switching. Light is the default and renders EXACTLY as it did before
// dark mode existed — every dark style lives under [data-theme='dark'], so with
// the attribute absent the stylesheet resolves to the original values.
//
// The attribute is set on <html> before React renders (initTheme() runs at
// module top of main.tsx) so a dark-preference reload never flashes light first.

const KEY = 'verityrx.theme';

export type Theme = 'light' | 'dark';

export function currentTheme(): Theme {
  return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
}

export function syncThemeColor(): void {
  const canvas = getComputedStyle(document.documentElement).getPropertyValue('--vx-canvas').trim();
  if (canvas) document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')?.setAttribute('content', canvas);
}

/** Switch themes with every transition held for one frame. Without this each
 *  control faded between palettes at its own speed (a 120ms background fade
 *  under an instant text colour), so for a moment light text sat on a light
 *  button, which is what an accessibility scan taken then reads, and what an
 *  eye sees as a flicker. A theme is a state change, not a motion. */
export function withoutTransitions(change: () => void): void {
  const root = document.documentElement;
  root.dataset.themeSwitching = '';
  change();
  void root.offsetHeight; // commit the new palette with transitions off
  requestAnimationFrame(() => requestAnimationFrame(() => { delete root.dataset.themeSwitching; }));
}

export function applyTheme(theme: Theme): void {
  withoutTransitions(() => applyThemeNow(theme));
  try {
    localStorage.setItem(KEY, theme);
  } catch {
    // Storage can be unavailable (private mode); the toggle still works for
    // the session, it just won't persist.
  }
}

function applyThemeNow(theme: Theme): void {
  if (theme === 'dark') {
    document.documentElement.dataset.theme = 'dark';
  } else {
    // Remove rather than set 'light': the light stylesheet is the UNSCOPED
    // default, so absence of the attribute IS light mode. Setting a value would
    // invite someone to scope light styles to it and fork the baseline.
    delete document.documentElement.dataset.theme;
  }
  syncThemeColor();
}

export function initTheme(): void {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(KEY);
  } catch {
    stored = null;
  }
  // Deliberately NOT consulting prefers-color-scheme: light is the product's
  // baseline until someone chooses otherwise, per the owner's call.
  if (stored === 'dark') applyTheme('dark');
}

export function toggleTheme(): Theme {
  const next: Theme = currentTheme() === 'dark' ? 'light' : 'dark';
  applyTheme(next);
  return next;
}
